import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import InvestmentCalculator from './InvestmentCalculator';
import GetInTouch from './GetInTouch';
import contactInfo, { getWhatsAppLink } from '../config/contactInfo';

const LandingPage = () => {
    const [activeTab, setActiveTab] = useState('construction');
    const [menuOpen, setMenuOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const [activeSection, setActiveSection] = useState('hero');

    useEffect(() => {
        const handleScroll = () => {
            setScrolled(window.scrollY > 50);

            // Track active section
            const sections = ['hero', 'about', 'why-us', 'services', 'packages', 'reviews', 'contact'];
            for (let i = sections.length - 1; i >= 0; i--) {
                const el = document.getElementById(sections[i]);
                if (el) {
                    const rect = el.getBoundingClientRect();
                    if (rect.top <= 120) {
                        setActiveSection(sections[i]);
                        break;
                    }
                }
            }
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const smoothScrollTo = (targetY, duration = 1400) => {
        const startY = window.pageYOffset;
        const diff = targetY - startY;
        let startTime = null;

        const easeInOutQuart = (t) => {
            return t < 0.5
                ? 8 * t * t * t * t
                : 1 - Math.pow(-2 * t + 2, 4) / 2;
        };

        const step = (timestamp) => {
            if (!startTime) startTime = timestamp;
            const elapsed = timestamp - startTime;
            const progress = Math.min(elapsed / duration, 1);
            const easedProgress = easeInOutQuart(progress);
            window.scrollTo(0, startY + diff * easedProgress);
            if (elapsed < duration) {
                requestAnimationFrame(step);
            }
        };

        requestAnimationFrame(step);
    };

    const handleNavClick = (e, targetId) => {
        e.preventDefault();
        setMenuOpen(false);
        const element = document.getElementById(targetId);
        if (element) {
            const navHeight = 75;
            const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
            smoothScrollTo(elementPosition - navHeight, 1000);
        }
    };

    const packages = [
        {
            category: 'construction',
            tier: 'Essential',
            title: 'Construction',
            price: '1,850',
            unit: 'per sq ft',
            desc: 'Perfect for budget-conscious homeowners',
            features: ['Standard construction materials', 'Basic structural design', 'Standard electrical & plumbing', 'Basic finishing', '1-year warranty'],
            featured: false,
            badge: null,
            tierClass: '',
        },
        {
            category: 'construction',
            tier: 'Premium',
            title: 'Construction',
            price: '2,450',
            unit: 'per sq ft',
            desc: 'Most popular choice for quality homes',
            features: ['Premium construction materials', 'Advanced structural design', 'Branded electrical & plumbing', 'Premium finishing', '3-year warranty', 'Free consultation'],
            featured: true,
            badge: 'Most Popular',
            tierClass: '',
        },
        {
            category: 'construction',
            tier: 'Luxury',
            title: 'Construction',
            price: '3,250',
            unit: 'per sq ft',
            desc: 'Ultimate luxury and customization',
            features: ['Luxury imported materials', 'Custom architectural design', 'Smart home integration', 'Designer finishing', '5-year warranty', 'Dedicated project manager'],
            featured: false,
            badge: null,
            tierClass: 'luxury',
        },
        {
            category: 'interior',
            tier: 'Classic',
            title: 'Interior',
            price: '1,150',
            unit: 'per sq ft',
            desc: 'Elegant interiors on a budget',
            features: ['Basic modular kitchen', 'Standard wardrobes', 'False ceiling (selected areas)', 'Basic lighting', 'Painting & wall treatment'],
            featured: false,
            badge: null,
            tierClass: '',
        },
        {
            category: 'interior',
            tier: 'Designer',
            title: 'Interior',
            price: '1,850',
            unit: 'per sq ft',
            desc: 'Stylish and functional spaces',
            features: ['Premium modular kitchen', 'Designer wardrobes', 'Complete false ceiling', 'Designer lighting', 'Imported wall coverings', 'Custom furniture'],
            featured: true,
            badge: 'Most Popular',
            tierClass: '',
        },
        {
            category: 'renovation',
            tier: 'Quick Refresh',
            title: 'Renovation',
            price: '950',
            unit: 'per sq ft',
            desc: 'Fast and affordable updates',
            features: ['Painting & touch-ups', 'Minor repairs', 'Fixture replacement', 'Basic upgrades', 'Quick turnaround'],
            featured: false,
            badge: null,
            tierClass: '',
        },
    ];

    const filteredPackages = activeTab === 'all'
        ? packages
        : packages.filter(pkg => pkg.category === activeTab);

    return (
        <div className="landing-page">
            <nav className={`navbar ${scrolled ? 'navbar-scrolled' : ''}`}>
                <div className="nav-container">
                    <h1 className="logo">
                        <span className="logo-icon">
                            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M3 21h18"></path>
                                <path d="M5 21V7l7-4 7 4v14"></path>
                                <path d="M9 21v-6h6v6"></path>
                                <path d="M10 9h4"></path>
                                <path d="M10 13h4"></path>
                            </svg>
                        </span>
                        BRIQ
                    </h1>

                    <button
                        className={`hamburger ${menuOpen ? 'active' : ''}`}
                        onClick={() => setMenuOpen(!menuOpen)}
                        aria-label="Toggle menu"
                    >
                        <span className="hamburger-line"></span>
                        <span className="hamburger-line"></span>
                        <span className="hamburger-line"></span>
                    </button>

                    {menuOpen && <div className="nav-overlay" onClick={() => setMenuOpen(false)}></div>}

                    <ul className={`nav-links ${menuOpen ? 'nav-open' : ''}`}>
                        <li><a href="#hero" className={activeSection === 'hero' ? 'nav-active' : ''} onClick={(e) => handleNavClick(e, 'hero')}>Home</a></li>
                        <li><a href="#about" className={activeSection === 'about' ? 'nav-active' : ''} onClick={(e) => handleNavClick(e, 'about')}>About</a></li>
                        <li><a href="#why-us" className={activeSection === 'why-us' ? 'nav-active' : ''} onClick={(e) => handleNavClick(e, 'why-us')}>Why Us</a></li>
                        <li><a href="#services" className={activeSection === 'services' ? 'nav-active' : ''} onClick={(e) => handleNavClick(e, 'services')}>Services</a></li>
                        <li><a href="#packages" className={activeSection === 'packages' ? 'nav-active' : ''} onClick={(e) => handleNavClick(e, 'packages')}>Packages</a></li>
                        <li><a href="#reviews" className={activeSection === 'reviews' ? 'nav-active' : ''} onClick={(e) => handleNavClick(e, 'reviews')}>Reviews</a></li>
                        <li><a href="#contact" className={activeSection === 'contact' ? 'nav-active' : ''} onClick={(e) => handleNavClick(e, 'contact')}>Contact</a></li>
                        <li><a href={getWhatsAppLink("Hi! I want to know more about Briq Construction services.")} target="_blank" rel="noopener noreferrer" className="nav-whatsapp">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                            </svg>
                            WhatsApp
                        </a></li>
                    </ul>
                </div>
            </nav>
            <section className="hero" id="hero">
                <div className="hero-content">
                    <h1>Crafting Signature Spaces</h1>
                    <p>Professional construction services tailored to your vision. From renovations to custom builds, we deliver quality, reliability, and innovation. Discover our packages and start your project today.</p>
                    <a href="#packages" className="cta-button" onClick={(e) => handleNavClick(e, 'packages')}>View Packages</a>
                </div>
            </section>
            <section className="about-us" id="about">
                <div className="about-text">
                    <h2>About BRIQ Construction</h2>
                    <p>Founded in 2003, BRIQ Construction specializes in residential and commercial renovations, wall paintings, and custom builds. Our team of experts ensures every project is completed with precision and care, transforming spaces into dream homes and offices.</p>
                    <p>We pride ourselves on using eco-friendly materials and sustainable practices. With over 500 successful projects, we have built a reputation for excellence in the construction industry. Our mission is to provide exceptional service that exceeds expectations, from initial consultation to final walkthrough.</p>
                    <ul>
                        <li>Certified professionals with 20+ years experience</li>
                        <li>100% satisfaction guarantee</li>
                        <li>Free consultations and estimates</li>
                        <li>Eco-friendly and sustainable materials</li>
                    </ul>
                </div>
            </section>
            <section className="why-choose-us" id="why-us">
                <h2>Why Choose BRIQ?</h2>
                <p className="why-choose-subtitle">Trusted by 500+ families across India to build their dream spaces</p>
                <div className="why-grid">
                    <div className="why-card why-card-highlight">
                        <div className="why-card-badge">🏆</div>
                        <h3>25+ Years Experience</h3>
                        <p>Over two decades of crafting exceptional spaces. Our seasoned team has delivered 500+ successful projects with unmatched precision and reliability.</p>
                        <div className="why-card-stat">
                            <span className="stat-number">100+</span>
                            <span className="stat-label">Projects Delivered</span>
                        </div>
                    </div>
                    <div className="why-card">
                        <div className="why-card-badge">✅</div>
                        <h3>Quality Assurance</h3>
                        <p>We ensure top-notch materials and workmanship. Every project undergoes rigorous quality checks to guarantee 100% satisfaction.</p>
                        <div className="why-card-stat">
                            <span className="stat-number">100%</span>
                            <span className="stat-label">Satisfaction Rate</span>
                        </div>
                    </div>
                    <div className="why-card">
                        <div className="why-card-badge">📈</div>
                        <h3>Better ROI</h3>
                        <p>Our properties deliver 12–16% annual appreciation, outperforming FDs and mutual funds. Invest smart, build wealth with real estate.</p>
                        <div className="why-card-stat">
                            <span className="stat-number">14%</span>
                            <span className="stat-label">Avg. Annual Returns</span>
                        </div>
                    </div>
                    <div className="why-card">
                        <div className="why-card-badge">⏰</div>
                        <h3>Timely Delivery</h3>
                        <p>Projects completed on time, every time. We respect your schedule and budget with transparent communication and zero hidden costs.</p>
                        <div className="why-card-stat">
                            <span className="stat-number">98%</span>
                            <span className="stat-label">On-Time Delivery</span>
                        </div>
                    </div>
                </div>
            </section>
            <section className="services" id="services">
                <h2>Our Services</h2>
                <p className="services-subtitle">Comprehensive construction & design solutions powered by expertise and innovation</p>
                <div className="services-grid">
                    <div className="service-card">
                        <div className="service-icon">🏗️</div>
                        <h3>End-to-End Home Construction</h3>
                        <p>Complete construction services from foundation to finishing. We handle every aspect of your dream home with precision and quality.</p>
                        <ul>
                            <li>Architectural Planning & Design</li>
                            <li>Structural Engineering</li>
                            <li>Construction Management</li>
                            <li>Quality Material Sourcing</li>
                            <li>On-time Project Delivery</li>
                        </ul>
                    </div>
                    <div className="service-card">
                        <div className="service-icon">🎨</div>
                        <h3>Interior Design & Execution</h3>
                        <p>Transform your space with our premium interior design services. From concept to completion, we create stunning interiors.</p>
                        <ul>
                            <li>Custom Interior Design</li>
                            <li>Modular Kitchen & Wardrobes</li>
                            <li>False Ceiling & Lighting</li>
                            <li>Flooring & Wall Treatments</li>
                            <li>Furniture & Décor</li>
                        </ul>
                    </div>
                    <div className="service-card">
                        <div className="service-icon">🔧</div>
                        <h3>Renovation & Remodeling</h3>
                        <p>Breathe new life into your existing property. Our renovation services enhance value and functionality.</p>
                        <ul>
                            <li>Complete Home Makeover</li>
                            <li>Kitchen & Bathroom Renovation</li>
                            <li>Structural Repairs</li>
                            <li>Space Optimization</li>
                            <li>Modern Upgrades</li>
                        </ul>
                    </div>
                    <div className="service-card">
                        <div className="service-icon">🤖</div>
                        <h3>AI-Powered Map & Design</h3>
                        <p>Leverage cutting-edge AI technology for precise house map designing and intelligent interior visualization before construction begins.</p>
                        <ul>
                            <li>AI-Generated Floor Plans</li>
                            <li>3D Home Visualization</li>
                            <li>Smart Space Planning</li>
                            <li>AI Interior Style Preview</li>
                            <li>Vastu-Compliant Layouts</li>
                        </ul>
                    </div>
                </div>
            </section>
            <InvestmentCalculator />
            <section id="packages" className="packages">
                <h2>Our Packages</h2>
                <p className="packages-subtitle">Choose the perfect plan for your dream construction project</p>

                <div className="packages-tabs">
                    <button
                        className={`packages-tab ${activeTab === 'all' ? 'active' : ''}`}
                        onClick={() => setActiveTab('all')}
                    >All</button>
                    <button
                        className={`packages-tab ${activeTab === 'construction' ? 'active' : ''}`}
                        onClick={() => setActiveTab('construction')}
                    >Construction</button>
                    <button
                        className={`packages-tab ${activeTab === 'interior' ? 'active' : ''}`}
                        onClick={() => setActiveTab('interior')}
                    >Interior</button>
                    <button
                        className={`packages-tab ${activeTab === 'renovation' ? 'active' : ''}`}
                        onClick={() => setActiveTab('renovation')}
                    >Renovation</button>
                </div>

                <div className="packages-grid">
                    {filteredPackages.map((pkg, index) => (
                        <div className={`package-card ${pkg.featured ? 'featured' : ''}`} key={index}>
                            {pkg.badge && <div className="package-badge">{pkg.badge}</div>}
                            <div className={`package-tier ${pkg.tierClass}`}>{pkg.tier}</div>
                            <h3>{pkg.title}</h3>
                            <div className="package-price">
                                <span className="price-currency">₹</span>
                                <span className="price-amount">{pkg.price}</span>
                                <span className="price-unit">{pkg.unit}</span>
                            </div>
                            <p className="package-desc">{pkg.desc}</p>
                            <ul>
                                {pkg.features.map((f, i) => <li key={i}>{f}</li>)}
                            </ul>
                            <a
                                href={getWhatsAppLink(`Hi! I'm interested in the ${pkg.tier} ${pkg.title} package (₹${pkg.price} ${pkg.unit}). Please share more details.`)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="package-button"
                            >
                                Get Quote
                            </a>
                        </div>
                    ))}
                </div>
            </section>
            <section className="client-reviews" id="reviews">
                <h2>What Our Clients Say</h2>
                <p className="reviews-subtitle">Real stories from real homeowners who trusted BRIQ</p>
                <div className="reviews-grid">
                    <div className="review-card">
                        <div className="review-quote">"</div>
                        <p className="review-text">briq transformed our vision into reality. The quality of construction and attention to detail was exceptional. Our dream home was delivered on time and within budget.</p>
                        <div className="review-project">🏠 3BHK Villa Construction</div>
                        <div className="review-divider"></div>
                        <div className="review-author">
                            <div className="review-avatar">RK</div>
                            <div className="review-info">
                                <span className="review-name">Rajesh Kumar</span>
                                <span className="review-location">📍 Gurugram</span>
                            </div>
                            <div className="review-stars">★★★★★</div>
                        </div>
                    </div>
                    <div className="review-card review-card-accent">
                        <div className="review-quote">"</div>
                        <p className="review-text">The renovation work done by briq exceeded our expectations. The team was professional, and the project management was seamless. Highly recommend their services!</p>
                        <div className="review-project">🔧 Complete Home Renovation</div>
                        <div className="review-divider"></div>
                        <div className="review-author">
                            <div className="review-avatar">PS</div>
                            <div className="review-info">
                                <span className="review-name">Priya Sharma</span>
                                <span className="review-location">📍 Gurugram</span>
                            </div>
                            <div className="review-stars">★★★★★</div>
                        </div>
                    </div>
                    <div className="review-card">
                        <div className="review-quote">"</div>
                        <p className="review-text">Best decision to choose briq for our interior work. The designers understood our style perfectly, and the execution was flawless. Our home looks stunning!</p>
                        <div className="review-project">🎨 Luxury Interior Design</div>
                        <div className="review-divider"></div>
                        <div className="review-author">
                            <div className="review-avatar">AV</div>
                            <div className="review-info">
                                <span className="review-name">Amit Verma</span>
                                <span className="review-location">📍 Gurugram</span>
                            </div>
                            <div className="review-stars">★★★★★</div>
                        </div>
                    </div>
                    <div className="review-card review-card-accent">
                        <div className="review-quote">"</div>
                        <p className="review-text">Working with briq was a pleasure. Their transparency in pricing and commitment to quality made the entire construction process stress-free. Excellent ROI on our investment!</p>
                        <div className="review-project">🏗️ 4BHK Independent House</div>
                        <div className="review-divider"></div>
                        <div className="review-author">
                            <div className="review-avatar">NG</div>
                            <div className="review-info">
                                <span className="review-name">Neha Gupta</span>
                                <span className="review-location">📍 Gurugram</span>
                            </div>
                            <div className="review-stars">★★★★★</div>
                        </div>
                    </div>
                </div>
            </section>
            <GetInTouch />
            <footer className="footer">
                <p>&copy; 2023 BRIQ Construction. All rights reserved. <Link to="/investment-calculator">Invest with us</Link></p>
            </footer>
        </div>
    );
};

export default LandingPage;